'use client';

import { c as _c } from "react/compiler-runtime";
import { Divider, Stack, Typography } from '@mui/material';
import { ResourceType } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ResourceTypeIndicator } from '@/components/cloudProviderIndicator';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { Link } from '@/components/shared/link';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { getDocsUrl } from '@/docs/docsUrls';
import { pluralize } from '@/utils/string';
export const highFrequencyPolicyResourceTypeLimitations: Partial<Record<ResourceType, {
  docsUrl: string;
  ff: string;
}>> = {
  [ResourceType.AwsEc2]: {
    docsUrl: getDocsUrl('ec2EncryptedVolumeAccess'),
    ff: 'Ec2HighFrequencyExcludePlatformEncryption'
  }
};
type ResourceTypeLimitations = Partial<Record<ResourceType, {
  docsUrl: string;
  ff: string;
}>>;
export const SupportedResourceTypesText = t0 => {
  const $ = _c(21);
  const {
    resourceTypes,
    resourceTypeLimitations
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsOpen(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = resourceTypes?.length;
  const t3 = resourceTypes?.length;
  let t4;
  if ($[1] !== t3) {
    t4 = pluralize("resource type", t3);
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t2 || $[4] !== t4) {
    t5 = <Typography component="span" className="underline cursor-pointer" onClick={t1}>{t2}{" "}{t4}</Typography>;
    $[3] = t2;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      hideBorder: true,
      title: "Available resource types",
      onClose: () => setIsOpen(false)
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      maxHeight: "60vh"
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Divider sx={{
      borderStyle: "dashed"
    }} />;
    t9 = {
      overflowY: "auto",
      gap: "12px"
    };
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] !== resourceTypeLimitations || $[11] !== resourceTypes) {
    t10 = resourceTypes?.map(resourceType => <ResourceTypeRow key={resourceType} resourceType={resourceType} resourceTypeLimitations={resourceTypeLimitations} />);
    $[10] = resourceTypeLimitations;
    $[11] = resourceTypes;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10) {
    t11 = <Stack direction="column" divider={t8} sx={t9}>{t10}</Stack>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== isOpen || $[16] !== t11) {
    t12 = <MainDialog isOpen={isOpen} header={t6} isLoading={false} sx={t7}>{t11}</MainDialog>;
    $[15] = isOpen;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t5) {
    t13 = <>{t5}{t12}</>;
    $[18] = t12;
    $[19] = t5;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  return t13;
};
const ResourceTypeRow = t0 => {
  const $ = _c(13);
  const {
    resourceType,
    resourceTypeLimitations
  } = t0;
  const resourceTypeConfig = resourceTypeLimitations?.[resourceType];
  const docsUrl = resourceTypeConfig?.docsUrl;
  const allFlags = useFeatureFlags();
  const isLimited = resourceTypeConfig?.ff ? allFlags[resourceTypeConfig.ff] : true;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      minHeight: "32px",
      justifyContent: "center",
      paddingX: "40px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      justifyContent: "space-between"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== resourceType) {
    t3 = <ResourceTypeIndicator resourceType={resourceType} />;
    $[2] = resourceType;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== docsUrl || $[5] !== isLimited) {
    t4 = isLimited && docsUrl && <Stack direction="row" sx={{
      gap: "24px",
      alignItems: "center"
    }}><Typography>Some limitations apply</Typography><ButtonWithIcon icon="material-symbols-book-ribbon-outline-rounded" component={Link} href={docsUrl} target="_blank" rel="noopener noreferrer" text="View" /></Stack>;
    $[4] = docsUrl;
    $[5] = isLimited;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <Stack direction="row" sx={t2}>{t3}{t4}</Stack>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== resourceType || $[11] !== t5) {
    t6 = <Stack key={resourceType} sx={t1}>{t5}</Stack>;
    $[10] = resourceType;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export const AvailableForText = t0 => {
  const $ = _c(5);
  const {
    resourceTypes,
    resourceTypeLimitations
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "4px",
      alignItems: "center"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Typography align="center" sx={{
      padding: "12px 0px"
    }}>Available for</Typography>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== resourceTypeLimitations || $[3] !== resourceTypes) {
    t3 = <Stack direction="row" sx={t1}>{t2}<SupportedResourceTypesText resourceTypes={resourceTypes} resourceTypeLimitations={resourceTypeLimitations} /></Stack>;
    $[2] = resourceTypeLimitations;
    $[3] = resourceTypes;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};